<template>
  <Transition
    name="c-modal--fade-animation"
    @after-enter="onAfterEnter"
    @after-leave="onAfterLeave"
  >
    <dialog
      v-if="open"
      :id="getModalId"
      :class="b()"
    >
      <div
        ref="wrapper"
        :class="b('wrapper', { size, stickOnTop })"
      >
        <div :class="b('body')">
          <div :class="b('header')">
            <h5
              v-if="title"
              :class="b('title')"
            >
              {{ title }}
            </h5>
            <button
              v-if="closeable"
              :class="b('close')"
              :aria-label="$t('c-modal.closeTitle')"
              type="button"
              @click.prevent="closeModal"
            >
              <e-icon-sprite
                icon="i-close"
                size="24"
              />
            </button>
          </div>

          <div :class="b('notifications')">
            <c-notification-container display-type="modal" />
          </div>

          <div :class="[b('content', { spacing }), contentClass]">
            <!-- @slot The modals main content. -->
            <slot></slot>
          </div>
        </div>
      </div>
    </dialog>
  </Transition>
</template>

<script>
  import cNotificationContainer from '@/components/c-notification-container.vue';
  import useUuid from '@/compositions/uuid.js';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import propScale from '@/helpers/prop.scale.js';

  /**
   * Model component.
   * You can give the component a custom header component. The scope content will be placed in the main
   * slot which represents the modal content.
   */
  export default {
    name: 'c-modal',

    components: {
      eIconSprite,
      cNotificationContainer,
    },
    // mixins: [],

    props: {
      /**
       * Allows to attach a title to the modal header.
       */
      title: {
        type: String,
        default: null,
      },

      /**
       * Set's the inner spacing of the modal [0, 500].
       */
      spacing: propScale(500, [0, 500]),

      /**
       * Defines size for modal [300, 500, 900].
       */
      size: propScale(500, [300, 500, 900, 1000]),

      /**
       * If 'true' adds a close icon to the header.
       */
      closeable: {
        type: Boolean,
        default: true,
      },

      /**
       * Toggles the visibility of the modal.
       */
      open: {
        type: Boolean,
        default: false,
      },

      /**
       * Adds additional class to the content element.
       */
      contentClass: {
        type: String,
        default: '',
      },

      /**
       * Determines if the scroll position should be restored after closing the modal.
       */
      restoreScrollPositionAfterClose: {
        type: Boolean,
        default: true,
      },
    },

    emits: ['close', 'open', 'update:open'],

    setup() {
      return {
        ...useUuid(),
      };
    },

    data() {
      return {
        scrollPositionY: 0,
        stickOnTop: false,
      };
    },
    // data() {
    //   return {};
    // },

    computed: {
      isClosable() {
        return this.open && this.closeable;
      },

      getModalId() {
        return `c-modal-${this.uuid}`;
      },
    },

    watch: {
      open: {
        immediate: true,
        handler(newValue, oldValue) {
          // Was needed in cases where the modal is initialized in open state.
          if (newValue) {
            this.$nextTick(() => {
              this.scrollPositionY = window.scrollY;

              this.openModal();
            });
          } else if (oldValue !== undefined) {
            // Only close if it was previously open.
            this.closeModal();
          }
        },
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      // Failsafe for cleanup when the component is being destroyed instead of made invisible.
      this.$bodyscroll.enableBodyScroll(this.$el);

      if (this.restoreScrollPositionAfterClose) {
        window.scrollTo(0, this.scrollPositionY);
      }
    },
    // unmounted() {},

    methods: {
      /**
       * Opens the modal.
       */
      openModal() {
        this.$nextTick(() => {
          this.$el.showModal(); // Native function of `HTMLDialogElement`.

          if (this.$el.contains(document.activeElement)) {
            document.activeElement.blur(); // Remove focus from the active element
          }

          this.$bodyscroll.disableBodyScroll(this.$el, { reserveScrollBarGap: true });
          this.$emit('update:open', true);
        });
      },

      /**
       * Close button event handler.
       */
      closeModal() {
        if (this.open) {
          this.$el.close(); // Native function of `HTMLDialogElement`.
          this.$emit('update:open', false);
        }

        this.$emit('close');

        this.$bodyscroll.enableBodyScroll(this.$el);

        if (this.restoreScrollPositionAfterClose) {
          window.scrollTo(0, this.scrollPositionY);
        }
      },

      /**
       * Handler for keypress events.
       *
       * @param {Object} event - The event object.
       */
      onKeyDown(event) {
        if (this.isClosable && event.code === 'Escape') {
          this.closeModal();
        }
      },

      /**
       * Handler when the user clicks outside.
       *
       * @param {Object} event - The event object.
       */
      onClickOutside(event) {
        if (this.isClosable && event.target.id === this.getModalId) {
          this.closeModal();
        }
      },

      /**
       * Handler for when the modal open-animation is completed.
       */
      onAfterEnter() {
        this.$emit('open');
        this.checkViewportSize();

        window.addEventListener('resize', this.checkViewportSize);
        document.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('click', this.onClickOutside);
      },

      /**
       * Handler for when the modal close-animation is completed.
       */
      onAfterLeave() {
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('click', this.onClickOutside);
        window.removeEventListener('resize', this.checkViewportSize);
      },

      checkViewportSize() {
        const { wrapper } = this.$refs;

        if (wrapper) {
          this.stickOnTop = window.innerHeight < wrapper.clientHeight;
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-modal {
    top: 0;
    left: 0;
    min-width: 100%;
    min-height: 100%;
    background: transparent;

    &::backdrop {
      opacity: 0.8;
      background-color: variables.$color-grayscale--100;
    }

    &__wrapper {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 100%;
      padding: 1.875rem 0;
      overflow: visible;
      border: none;
      transform: translate(-50%, -50%);
      text-align: left;

      @include mixins.media($down: xs) {
        max-width: 100%;
        margin: 0;
      }

      @include mixins.media(sm) {
        max-width: functions.rem(690px);
      }

      @include mixins.media(md) {
        max-width: functions.rem(1128px);
      }

      &--stick-on-top {
        top: 0;
        margin-right: 0;
        transform: translate(-50%, 0);
      }

      &--size-300 {
        @include mixins.media(sm) {
          max-width: functions.rem(690px);
        }

        @include mixins.media(md) {
          max-width: functions.rem(850px);
        }
      }

      &--size-900 {
        @include mixins.media(sm) {
          max-width: 95%;
        }

        @include mixins.media(lg) {
          max-width: map.get(variables.$breakpoints, lg) - (2 * variables.$spacing--30);
        }
      }

      &--size-1000 {
        @include mixins.media(sm) {
          max-width: 100%;
        }

        @include mixins.media(lg) {
          max-width: 85%;
        }
      }
    }

    &__body {
      background-color: variables.$color-grayscale--1000;
      box-shadow: 0 0 15px 0 variables.$color-grayscale--200;
    }

    &__header {
      display: flex;
      align-items: flex-start;
      padding: functions.rem(variables.$spacing--25 variables.$spacing--25 variables.$spacing--20);
    }

    &__title {
      margin: functions.rem(variables.$spacing--2 0 0); // Simulates vertical centering but allows multiline.
      padding-right: functions.rem(variables.$spacing--10);
    }

    &__close {
      @extend %button-reset;

      display: block;
      margin-left: auto;
      color: functions.get-theme-var('c-modal-close-color');

      .e-icon-sprite {
        width: functions.rem(23px);
        height: functions.rem(23px);
      }
    }

    &__content {
      &--spacing-0 {
        padding: 0;
      }

      &--spacing-500 {
        padding: functions.rem(variables.$spacing--40 variables.$spacing--35);

        @include mixins.media(sm) {
          padding: functions.rem(variables.$spacing--10 variables.$spacing--60 variables.$spacing--60);
        }

        @include mixins.media(md) {
          padding: functions.rem(variables.$spacing--20 variables.$spacing--100 variables.$spacing--100);
        }
      }
    }
  }

  .c-modal--fade-animation-enter-active,
  .c-modal--fade-animation-leave-active {
    &,
    &::backdrop {
      transition: opacity 200ms ease-in-out;
    }
  }

  .c-modal--fade-animation-enter-from,
  .c-modal--fade-animation-leave-to {
    &,
    &::backdrop {
      opacity: 0;
    }
  }
</style>
